import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { W_BillingCode_FindGroups_VM_Single } from 'ab-api/web/billingCode/findGroups';

import type { BillingCodeGroupVM, BillingCodeVM } from 'ab-viewModels/jobPreview.viewModel';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';

import ColorSquare from 'af-components/ColorSquare';
import SimpleTable from 'af-components/Controls/SimpleTable/index';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import Tooltip from 'af-components/Tooltip';

import CLIENT from 'af-routes/client';

import * as BillingCodeActions from 'af-actions/billingCode';
import * as WorkRequestActions from 'af-actions/workRequests';

import styles from './styles.module.scss';
import type { JobPreviewOutletContext } from './types';

type Props = ConnectedProps<typeof connector>;

const renderTooltip = () => {
	return (
		<div>
			<strong>Archived Billing Code</strong>
			<div>
				This code has been archived and can no longer be used for new entries.<br />
				It remains visible here because it was used in past job work sheets.
			</div>
		</div>
	);
};

const BillingCodesTab: React.FC<Props> = ({ findAllBillingCodes, findAllBillingCodeGroups }) => {
	const navigate = useNavigate();
	const { jobId, hasPermissionToManageBillingCodes } = useOutletContext<JobPreviewOutletContext>();

	const [billingCodes, setBillingCodes] = React.useState<Nullable<BillingCodeVM[]>>(null);
	const [billingCodeGroups, setBillingCodeGroups] = React.useState<W_BillingCode_FindGroups_VM_Single[]>([]);

	const renderBillingCodesTextCell = React.useCallback((cell: CellContext<BillingCodeVM, string>) => {
		return (
			<div className={`${styles['text-cell']} ${cell.row.original.deletedAt ? styles.archived : ''}`}>
				{cell.getValue() ?? 'N/A'}
			</div>
		);
	}, []);

	const renderBillingCodesGroupCell = React.useCallback((cell: CellContext<BillingCodeVM, Nullable<BillingCodeGroupVM>>) => {
		const group = cell.getValue();

		if (!group) {
			return <div className={`${cell.row.original.deletedAt ? styles.archived : ''}`}>N/A</div>;
		}

		if (group.color) {
			return (
				<div className={`${styles['billing-code-group-cell']} ${cell.row.original.deletedAt ? styles.archived : ''}`}>
					<ColorSquare color={group.color} />
					{group.name}
				</div>
			);
		}

		return <div className={`${cell.row.original.deletedAt ? styles.archived : ''}`}>{group.name ?? 'N/A'}</div>;
	}, []);

	const renderArchivedCell = React.useCallback((cell: CellContext<BillingCodeVM, Nullable<Date>>) => {
		const row = cell.row.original;

		if (row.deletedAt) {
			return (
				<Tooltip message={renderTooltip()}>
					<span className={`${styles.archived} ${styles['archive-icon']}`}>
						<span className="icon-archive" />
					</span>
				</Tooltip>
			);
		}

		return null;
	}, []);

	const renderColorCell = React.useCallback((cell: CellContext<W_BillingCode_FindGroups_VM_Single, Nullable<ColorPalette>>) => {
		const color = cell.getValue();
		return (
			color ? <ColorSquare color={color} /> : null
		);
	}, []);

	const renderGroupsTextCell = React.useCallback((cell: CellContext<W_BillingCode_FindGroups_VM_Single, string>) => {
		return (
			cell.getValue() ?? 'N/A'
		);
	}, []);

	const fetchAndInitializeBillingCodeGroups = React.useCallback(async () => {
		if (!hasPermissionToManageBillingCodes) {
			navigate(CLIENT.ERROR.ERR403());
		}
		const fetchedBillingCodeGroups = await findAllBillingCodeGroups(jobId);
		setBillingCodeGroups(fetchedBillingCodeGroups);
	}, [findAllBillingCodeGroups, hasPermissionToManageBillingCodes, jobId, navigate]);

	const fetchAndInitializeBillingCodes = React.useCallback(async () => {
		if (!hasPermissionToManageBillingCodes) {
			navigate(CLIENT.ERROR.ERR403());
		}
		const fetchedBillingCodes = await findAllBillingCodes(jobId, { page: 0, pageSize: 10000 });
		setBillingCodes(fetchedBillingCodes.rows);
	}, [findAllBillingCodes, hasPermissionToManageBillingCodes, jobId, navigate]);

	React.useEffect(() => {
		fetchAndInitializeBillingCodes();
		fetchAndInitializeBillingCodeGroups();
	}, [fetchAndInitializeBillingCodes, fetchAndInitializeBillingCodeGroups]);

	const billingCodeGroupColumns: SimpleTableProps<W_BillingCode_FindGroups_VM_Single>['columns'] = React.useMemo(() => [
		{
			id: 'sortIndex',
			accessor: 'sortIndex',
			header: '',
			cell: renderGroupsTextCell,
			enableSorting: false,
			size: '40px',
		},
		{
			id: 'name',
			accessor: 'name',
			header: 'Name',
			cell: renderGroupsTextCell,
			enableSorting: false,
			size: '2fr',
		},
		{
			id: 'color',
			accessor: 'color',
			header: 'Color',
			cell: renderColorCell,
			enableSorting: false,
			size: '80px',
		},
	], [renderColorCell, renderGroupsTextCell]);

	const billingCodeColumns: SimpleTableProps<BillingCodeVM>['columns'] = React.useMemo(() => [
		{
			id: 'lineItemNumber',
			accessor: 'lineItemNumber',
			header: 'Line Item',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'customerNumber',
			accessor: 'customerNumber',
			header: 'Customer No.',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'customerId',
			accessor: 'customerId',
			header: 'Customer ID',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'ownerNumber',
			accessor: 'ownerNumber',
			header: 'Owner Number',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'ownerId',
			accessor: 'ownerId',
			header: 'Owner ID',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'bidQuantity',
			accessor: 'bidQuantity',
			header: 'Bid Qty',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'unit',
			accessor: 'unit',
			header: 'Unit',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'unitPrice',
			accessor: 'unitPrice',
			header: 'Unit Price',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'group',
			accessor: 'group',
			header: 'Group',
			cell: renderBillingCodesGroupCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'description',
			accessor: 'description',
			header: 'Description',
			cell: renderBillingCodesTextCell,
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'deletedAt',
			accessor: 'deletedAt',
			header: '',
			cell: renderArchivedCell,
			enableSorting: false,
			size: '36px',
		},
	], [renderArchivedCell, renderBillingCodesGroupCell, renderBillingCodesTextCell]);

	if (!billingCodes) {
		return null;
	}

	return (
		<div className={styles['job-preview__tab-content']}>
			<div className={styles['job-preview__tab-content__section']}>
				<SimpleTable
					columns={billingCodeGroupColumns}
					label="Billing Code Groups"
					// labelTooltip="A categorization layer for organizing related billing codes. In the Job Tracking table,
					// codes are grouped and sorted by their associated group, as listed here."
					rows={billingCodeGroups}
				/>
			</div>
			<div className={styles['job-preview__tab-content__section']}>
				<SimpleTable
					columns={billingCodeColumns}
					highlightDeletedRows={true}
					label="Billing Codes"
					// labelTooltip="Charge rates assigned to billable work items for this job. Billing codes are required fields for billable work
					// on both the Field Report Work Summary and the Jobs Work Summary."
					rows={billingCodes}
				/>
			</div>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findAllBillingCodes: WorkRequestActions.findAllBillingCodesTable,
		findAllBillingCodeGroups: BillingCodeActions.getGroups,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	connector,
	React.memo
);

export default enhance(BillingCodesTab);
